import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Client, Deal, OrderItem, Sample, Quote, Pipeline, PipelineStage, clientsAPI, dealsAPI, samplesAPI, quotesAPI, pipelineAPI } from '../../services/api';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ShoppingCartIcon,
  CalendarIcon,
  SparklesIcon,
  BeakerIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  StarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import ActivityTimeline from '../activities/ActivityTimeline';
import ActivityForm from '../activities/ActivityForm';
import EmailComposer from '../emails/EmailComposer';
import QuoteModal from '../deals/QuoteModal';
import QuoteDetailModal from '../deals/QuoteDetailModal';
import TaskList from '../tasks/TaskList';
import TaskFormModal from '../tasks/TaskFormModal';
import ClientSegments from './ClientSegments';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Avatar,
  Spinner,
  EmptyState,
  Modal,
} from '../ui';

const ClientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Helper function to generate correct paths
  const getClientPath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };

  // Phase 2: Activity Timeline
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  // Phase 4: Email Composer
  const [showEmailComposer, setShowEmailComposer] = useState(false);

  // Phase 5.3: Tabbed Interface
  const [activeTab, setActiveTab] = useState<'overview' | 'activity' | 'deals' | 'samples' | 'quotes' | 'tasks'>('overview');

  // Samples
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loadingSamples, setLoadingSamples] = useState(false);

  // Quotes
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedDealForQuote, setSelectedDealForQuote] = useState<Deal | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);

  // Tasks
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  // Inline editing states
  const [editingPipeline, setEditingPipeline] = useState(false);
  const [editingLifecycle, setEditingLifecycle] = useState(false);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | undefined>(undefined);
  const [selectedStageId, setSelectedStageId] = useState<number | undefined>(undefined);
  const [lifecycleForm, setLifecycleForm] = useState<{
    lifecycleStage: 'SUBSCRIBER' | 'LEAD' | 'MQL' | 'SQL' | 'OPPORTUNITY' | 'CUSTOMER' | 'EVANGELIST' | 'OTHER';
    leadStatus: 'NEW' | 'ATTEMPTING_TO_CONTACT' | 'CONNECTED' | 'OPEN_DEAL' | 'UNQUALIFIED' | 'BAD_TIMING' | 'LOST';
    leadScore: number;
  }>({
    lifecycleStage: 'LEAD',
    leadStatus: 'NEW',
    leadScore: 0,
  });
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [savingLifecycle, setSavingLifecycle] = useState(false);

  // Date editing states
  const [editingDates, setEditingDates] = useState(false);
  const [dateForm, setDateForm] = useState({
    lastContactDate: '',
    nextFollowUpDate: '',
  });
  const [savingDates, setSavingDates] = useState(false);

  // Tags editing states
  const [editingTags, setEditingTags] = useState(false);
  const [tagsForm, setTagsForm] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [savingTags, setSavingTags] = useState(false);

  useEffect(() => {
    if (id) {
      fetchClient();
      fetchPipelines();
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'samples') {
      fetchSamples();
    }
  }, [id, activeTab]);

  useEffect(() => {
    if (client && activeTab === 'quotes') {
      fetchQuotes();
    }
  }, [client, activeTab]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const clientData = await clientsAPI.getById(Number(id));
      setClient(clientData);
      // Initialize inline editing states
      if (clientData.pipelineId) {
        setSelectedPipelineId(clientData.pipelineId);
        const stagesData = await pipelineAPI.getStages(clientData.pipelineId);
        setStages(stagesData);
      }
      if (clientData.stageId) {
        setSelectedStageId(clientData.stageId);
      }
      setLifecycleForm({
        lifecycleStage: clientData.lifecycleStage || 'LEAD',
        leadStatus: clientData.leadStatus || 'NEW',
        leadScore: clientData.leadScore || 0,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch client');
    } finally {
      setLoading(false);
    }
  };

  const fetchPipelines = async () => {
    try {
      const data = await pipelineAPI.getAll();
      setPipelines(data.filter(p => p.isActive));
    } catch (err) {
      console.error('Failed to fetch pipelines:', err);
    }
  };

  const fetchStages = async (pipelineId: number) => {
    try {
      const data = await pipelineAPI.getStages(pipelineId);
      setStages(data);
    } catch (err) {
      console.error('Failed to fetch stages:', err);
    }
  };

  const handlePipelineChange = async (pipelineId: number) => {
    setSelectedPipelineId(pipelineId);
    setSelectedStageId(undefined);
    await fetchStages(pipelineId);
  };

  const savePipelineStage = async () => {
    if (!client || !selectedPipelineId || !selectedStageId) return;

    setSavingPipeline(true);
    try {
      await clientsAPI.update(client.id, {
        pipelineId: selectedPipelineId,
        stageId: selectedStageId,
      });
      await fetchClient();
      setEditingPipeline(false);
    } catch (err: any) {
      console.error('Failed to update pipeline:', err);
    } finally {
      setSavingPipeline(false);
    }
  };

  const saveLifecycleInfo = async () => {
    if (!client) return;

    setSavingLifecycle(true);
    try {
      await clientsAPI.update(client.id, {
        lifecycleStage: lifecycleForm.lifecycleStage,
        leadStatus: lifecycleForm.leadStatus,
        leadScore: lifecycleForm.leadScore,
      });
      await fetchClient();
      setEditingLifecycle(false);
    } catch (err: any) {
      console.error('Failed to update lifecycle:', err);
    } finally {
      setSavingLifecycle(false);
    }
  };

  const cancelPipelineEdit = () => {
    setEditingPipeline(false);
    if (client) {
      setSelectedPipelineId(client.pipelineId || undefined);
      setSelectedStageId(client.stageId || undefined);
    }
  };

  const cancelLifecycleEdit = () => {
    setEditingLifecycle(false);
    if (client) {
      setLifecycleForm({
        lifecycleStage: client.lifecycleStage || 'LEAD',
        leadStatus: client.leadStatus || 'NEW',
        leadScore: client.leadScore || 0,
      });
    }
  };

  // Date editing functions
  const openDateEdit = () => {
    if (client) {
      setDateForm({
        lastContactDate: client.lastContactDate ? client.lastContactDate.split('T')[0] : '',
        nextFollowUpDate: client.nextFollowUpDate ? client.nextFollowUpDate.split('T')[0] : '',
      });
    }
    setEditingDates(true);
  };

  const saveDates = async () => {
    if (!client) return;

    setSavingDates(true);
    try {
      await clientsAPI.update(client.id, {
        lastContactDate: dateForm.lastContactDate || undefined,
        nextFollowUpDate: dateForm.nextFollowUpDate || undefined,
      });
      await fetchClient();
      setEditingDates(false);
    } catch (err: any) {
      console.error('Failed to update dates:', err);
    } finally {
      setSavingDates(false);
    }
  };

  const cancelDateEdit = () => {
    setEditingDates(false);
    if (client) {
      setDateForm({
        lastContactDate: client.lastContactDate ? client.lastContactDate.split('T')[0] : '',
        nextFollowUpDate: client.nextFollowUpDate ? client.nextFollowUpDate.split('T')[0] : '',
      });
    }
  };

  // Tags editing functions
  const openTagsEdit = () => {
    if (client) {
      setTagsForm(client.tags || []);
    }
    setNewTag('');
    setEditingTags(true);
  };

  const addTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (tag && !tagsForm.includes(tag)) {
      setTagsForm([...tagsForm, tag]);
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove: string) => {
    setTagsForm(tagsForm.filter(tag => tag !== tagToRemove));
  };

  const saveTags = async () => {
    if (!client) return;

    setSavingTags(true);
    try {
      await clientsAPI.update(client.id, {
        tags: tagsForm,
      });
      await fetchClient();
      setEditingTags(false);
    } catch (err: any) {
      console.error('Failed to update tags:', err);
    } finally {
      setSavingTags(false);
    }
  };

  const cancelTagsEdit = () => {
    setEditingTags(false);
    if (client) {
      setTagsForm(client.tags || []);
    }
    setNewTag('');
  };

  const fetchSamples = async () => {
    try {
      setLoadingSamples(true);
      const samplesData = await samplesAPI.getByClient(Number(id));
      setSamples(samplesData);
    } catch (err: any) {
      console.error('Failed to fetch samples:', err);
    } finally {
      setLoadingSamples(false);
    }
  };

  const fetchQuotes = async () => {
    if (!client) return;

    try {
      setLoadingQuotes(true);
      const clientQuotes = await quotesAPI.getAll({ clientId: client.id });
      setQuotes(clientQuotes);
    } catch (err: any) {
      console.error('Failed to fetch quotes:', err);
    } finally {
      setLoadingQuotes(false);
    }
  };

  const getQuoteStatusBadge = (status: string) => {
    const statusConfig = {
      DRAFT: { color: 'bg-neutral-100 text-neutral-800 border-neutral-200', icon: DocumentTextIcon, label: 'Draft' },
      SENT: { color: 'bg-primary-100 text-primary-800 border-primary-200', icon: PaperAirplaneIcon, label: 'Sent' },
      ACCEPTED: { color: 'bg-success-100 text-success-800 border-success-200', icon: CheckCircleIcon, label: 'Accepted' },
      REJECTED: { color: 'bg-error-100 text-error-800 border-error-200', icon: XCircleIcon, label: 'Rejected' },
      EXPIRED: { color: 'bg-warning-100 text-warning-800 border-warning-200', icon: ClockIcon, label: 'Expired' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
    };
    return symbols[currency] || '$';
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'CALL':
        return <PhoneIcon className="h-5 w-5" />;
      case 'EMAIL':
        return <EnvelopeIcon className="h-5 w-5" />;
      default:
        return <ChatBubbleLeftRightIcon className="h-5 w-5" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (type) {
      case 'CALL':
        return `${baseClasses} bg-primary-100 text-primary-800`;
      case 'EMAIL':
        return `${baseClasses} bg-success-100 text-success-800`;
      default:
        return `${baseClasses} bg-neutral-100 text-neutral-800`;
    }
  };

  const getImportanceBadge = (importance: string) => {
    const variants: Record<string, 'neutral' | 'primary' | 'warning' | 'error'> = {
      VERY_LOW: 'neutral',
      LOW: 'primary',
      MEDIUM: 'warning',
      HIGH: 'error',
      VERY_HIGH: 'error',
    };

    return (
      <Badge variant={variants[importance] || 'neutral'} size="sm">
        {importance.replace('_', ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!client) {
    return (
      <Card variant="bordered" className="max-w-md mx-auto mt-12">
        <CardBody>
          <EmptyState
            type="error"
            title="Client not found"
            description="The client you're looking for doesn't exist or has been deleted."
            action={
              <Link to={getClientPath("/clients")}>
                <Button variant="primary" leftIcon={<ArrowLeftIcon className="h-4 w-4" />}>
                  Back to clients
                </Button>
              </Link>
            }
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Card variant="bordered" className="border-error-200 bg-error-50">
          <CardBody className="py-3">
            <p className="text-sm text-error-700">{error}</p>
          </CardBody>
        </Card>
      )}

      {/* Header */}
      <Card variant="gradient">
        <CardBody className="p-6">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <Avatar name={client.name} size="xl" className="bg-white/20" />
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl sm:truncate">
                  {client.name}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="secondary" size="sm">
                    {client.type.toLowerCase()}
                  </Badge>
                  {getImportanceBadge(client.importance)}
                  {client.lifecycleStage && (
                    <Badge variant="primary" size="sm">
                      {client.lifecycleStage.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2 lg:mt-0 lg:ml-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddActivity(true)}
                leftIcon={<PhoneIcon className="h-4 w-4" />}
              >
                Log Call
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowEmailComposer(true)}
                leftIcon={<EnvelopeIcon className="h-4 w-4" />}
              >
                Send Email
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddActivity(true)}
                leftIcon={<CalendarIcon className="h-4 w-4" />}
              >
                Schedule Meeting
              </Button>
              <Link to={getClientPath(`/clients/${client.id}/edit`)}>
                <Button variant="outline" size="sm" leftIcon={<PencilIcon className="h-4 w-4" />}>
                  Edit
                </Button>
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Tab Navigation */}
      <Card variant="bordered">
        <div className="border-b border-neutral-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
              { id: 'activity', label: 'Activity', icon: ChatBubbleLeftRightIcon },
              { id: 'deals', label: 'Deals', icon: CurrencyDollarIcon, count: client?.deals?.length },
              { id: 'samples', label: 'Samples', icon: BeakerIcon, count: samples.length },
              { id: 'quotes', label: 'Quotes', icon: DocumentDuplicateIcon, count: quotes.length },
              { id: 'tasks', label: 'Tasks', icon: CheckCircleIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
                {tab.count ? (
                  <Badge variant="neutral" size="sm" className="ml-2">
                    {tab.count}
                  </Badge>
                ) : null}
              </button>
            ))}
          </nav>
        </div>
      </Card>

      {/* Tab Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Client Information */}
            <div className="lg:col-span-2 space-y-6">
              <Card variant="bordered">
                <CardHeader title="Client Information" />
                <CardBody>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Email Addresses</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {client.email ? (
                          <div className="space-y-2">
                            {[client.email, ...(client.additionalEmails || [])].map((email, index) => (
                              <div key={email.toLowerCase()} className="flex min-w-0 items-center gap-2">
                                <EnvelopeIcon className="h-4 w-4 flex-none text-neutral-400" />
                                <a href={`mailto:${email}`} className="truncate text-primary-600 hover:text-primary-500">
                                  {email}
                                </a>
                                {index === 0 && (
                                  <span className="text-xs font-medium text-neutral-500">Primary</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-neutral-400">No email provided</span>
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Phone Numbers</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {client.phoneNumbers.length > 0 ? (
                          <div className="space-y-1">
                            {client.phoneNumbers.map((phone, index) => (
                              <div key={index} className="flex items-center">
                                <PhoneIcon className="h-4 w-4 text-neutral-400 mr-2" />
                                <a href={`tel:${phone}`} className="text-primary-600 hover:text-primary-500">
                                  {phone}
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-neutral-400">No phone numbers provided</span>
                        )}
                      </dd>
                    </div>

                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-neutral-500">Address</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {client.address ? (
                          <div className="flex items-start">
                            <MapPinIcon className="h-4 w-4 text-neutral-400 mr-2 mt-0.5" />
                            <span>{client.address}</span>
                          </div>
                        ) : (
                          <span className="text-neutral-400">No address provided</span>
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Assigned Salesperson</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {client.assignedUser ? (
                          <div className="flex items-center">
                            <Avatar name={`${client.assignedUser.firstName} ${client.assignedUser.lastName}`} size="sm" className="mr-2" />
                            <span>{client.assignedUser.firstName} {client.assignedUser.lastName}</span>
                          </div>
                        ) : (
                          <span className="text-neutral-400">Unassigned</span>
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-neutral-500">Created</dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {new Date(client.createdAt).toLocaleDateString()}
                      </dd>
                    </div>

                    {client.notes && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-neutral-500">Notes</dt>
                        <dd className="mt-1 text-sm text-neutral-900 whitespace-pre-wrap">{client.notes}</dd>
                      </div>
                    )}
                  </dl>
                </CardBody>
              </Card>

              {/* Pipeline Stage Section */}
              <div className="bg-gradient-to-br from-secondary-50 to-primary-50 shadow-lg overflow-hidden sm:rounded-lg mt-6 border border-secondary-100">
                <div className="px-4 py-5 sm:px-6 bg-white bg-opacity-60 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-neutral-900 flex items-center">
                      <svg className="h-5 w-5 mr-2 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      Pipeline Stage
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      Current position in the sales pipeline
                    </p>
                  </div>
                  {!editingPipeline && (
                    <button
                      onClick={() => setEditingPipeline(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-secondary-50 hover:bg-secondary-100 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-1.5" />
                      Edit
                    </button>
                  )}
                </div>
                <div className="border-t border-secondary-100 px-4 py-5 sm:px-6 bg-white">
                  {editingPipeline ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Pipeline</label>
                          <select
                            value={selectedPipelineId || ''}
                            onChange={(e) => handlePipelineChange(parseInt(e.target.value))}
                            className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-secondary-500 focus:ring-secondary-500"
                          >
                            <option value="">Select Pipeline</option>
                            {pipelines.map((p) => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Stage</label>
                          <select
                            value={selectedStageId || ''}
                            onChange={(e) => setSelectedStageId(parseInt(e.target.value))}
                            className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-secondary-500 focus:ring-secondary-500"
                            disabled={!selectedPipelineId}
                          >
                            <option value="">Select Stage</option>
                            {stages.map((s) => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={cancelPipelineEdit}
                          className="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={savePipelineStage}
                          disabled={savingPipeline || !selectedPipelineId || !selectedStageId}
                          className="px-3 py-1.5 border border-transparent rounded-lg text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700 disabled:opacity-50"
                        >
                          {savingPipeline ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      {client.stage?.color && (
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: client.stage.color }}
                        />
                      )}
                      <div>
                        <p className="text-lg font-semibold text-neutral-900">
                          {client.stage?.name || 'Not assigned'}
                        </p>
                        {client.pipeline && (
                          <p className="text-sm text-neutral-500">
                            Pipeline: {client.pipeline.name}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Lifecycle & Lead Information (Phase 1.1) */}
              <div className="bg-gradient-to-br from-primary-50 to-secondary-50 shadow-lg overflow-hidden sm:rounded-lg mt-6 border border-primary-100">
                <div className="px-4 py-5 sm:px-6 bg-white bg-opacity-60 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-neutral-900 flex items-center">
                      <svg className="h-5 w-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      Lifecycle & Lead Information
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      Track the client's journey through your sales process
                    </p>
                  </div>
                  {!editingLifecycle && (
                    <button
                      onClick={() => setEditingLifecycle(true)}
                      className="inline-flex items-center px-3 py-1.5 border border-primary-300 rounded-lg text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-1.5" />
                      Edit
                    </button>
                  )}
                </div>
                <div className="border-t border-primary-100 px-4 py-5 sm:px-6 bg-white">
                  {editingLifecycle ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Lifecycle Stage</label>
                          <select
                            value={lifecycleForm.lifecycleStage}
                            onChange={(e) => setLifecycleForm({ ...lifecycleForm, lifecycleStage: e.target.value as typeof lifecycleForm.lifecycleStage })}
                            className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          >
                            <option value="SUBSCRIBER">Subscriber</option>
                            <option value="LEAD">Lead</option>
                            <option value="MQL">MQL</option>
                            <option value="SQL">SQL</option>
                            <option value="OPPORTUNITY">Opportunity</option>
                            <option value="CUSTOMER">Customer</option>
                            <option value="EVANGELIST">Evangelist</option>
                            <option value="OTHER">Other</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Lead Status</label>
                          <select
                            value={lifecycleForm.leadStatus}
                            onChange={(e) => setLifecycleForm({ ...lifecycleForm, leadStatus: e.target.value as typeof lifecycleForm.leadStatus })}
                            className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          >
                            <option value="NEW">New</option>
                            <option value="ATTEMPTING_TO_CONTACT">Attempting to Contact</option>
                            <option value="CONNECTED">Connected</option>
                            <option value="OPEN_DEAL">Open Deal</option>
                            <option value="UNQUALIFIED">Unqualified</option>
                            <option value="BAD_TIMING">Bad Timing</option>
                            <option value="LOST">Lost</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-1">Lead Score (0-100)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={lifecycleForm.leadScore}
                            onChange={(e) => setLifecycleForm({ ...lifecycleForm, leadScore: parseInt(e.target.value) || 0 })}
                            className="w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={cancelLifecycleEdit}
                          className="px-3 py-1.5 border border-neutral-300 rounded-lg text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={saveLifecycleInfo}
                          disabled={savingLifecycle}
                          className="px-3 py-1.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                        >
                          {savingLifecycle ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-2">Lifecycle Stage</dt>
                        <dd className="mt-1">
                          <div className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold shadow-sm ${client.lifecycleStage === 'CUSTOMER' ? 'bg-success-100 text-success-800 border border-success-200' :
                            client.lifecycleStage === 'OPPORTUNITY' ? 'bg-primary-100 text-primary-800 border border-primary-200' :
                              client.lifecycleStage === 'LEAD' ? 'bg-warning-100 text-warning-800 border border-warning-200' :
                                client.lifecycleStage === 'SUBSCRIBER' ? 'bg-secondary-100 text-secondary-800 border border-secondary-200' :
                                  'bg-neutral-100 text-neutral-800 border border-neutral-200'
                            }`}>
                            {client.lifecycleStage || 'LEAD'}
                          </div>
                        </dd>
                      </div>

                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-2">Lead Status</dt>
                        <dd className="mt-1">
                          {(() => {
                            const status = client.leadStatus;
                            let bgColor = 'bg-neutral-100 text-neutral-800 border border-neutral-200';

                            // Negative statuses (red)
                            if (status === 'UNQUALIFIED' || status === 'BAD_TIMING' || status === 'LOST') {
                              bgColor = 'bg-error-100 text-error-800 border border-error-200';
                            }
                            // Positive statuses (green)
                            else if (status === 'OPEN_DEAL') {
                              bgColor = 'bg-success-100 text-success-800 border border-success-200';
                            }
                            // In progress statuses (blue)
                            else if (status === 'CONNECTED') {
                              bgColor = 'bg-primary-100 text-primary-800 border border-primary-200';
                            }
                            // Attempting contact (yellow)
                            else if (status === 'ATTEMPTING_TO_CONTACT') {
                              bgColor = 'bg-warning-100 text-warning-800 border border-warning-200';
                            }

                            return (
                              <div className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold shadow-sm ${bgColor}`}>
                                {status ? status.replace(/_/g, ' ') : 'NEW'}
                              </div>
                            );
                          })()}
                        </dd>
                      </div>

                      <div>
                        <dt className="text-sm font-medium text-neutral-500 mb-2">Lead Score</dt>
                        <dd className="mt-1 text-sm text-neutral-900">
                          <div className="flex items-center">
                            <div className="flex-1 bg-neutral-200 rounded-full h-3 mr-3 max-w-xs">
                              <div
                                className={`h-3 rounded-full transition-all duration-300 ${(client.leadScore || 0) >= 80 ? 'bg-gradient-to-r from-success-400 to-success-600' :
                                  (client.leadScore || 0) >= 60 ? 'bg-gradient-to-r from-primary-400 to-primary-600' :
                                    (client.leadScore || 0) >= 40 ? 'bg-gradient-to-r from-warning-400 to-warning-600' :
                                      'bg-gradient-to-r from-error-400 to-error-600'
                                  }`}
                                style={{ width: `${client.leadScore || 0}%` }}
                              ></div>
                            </div>
                            <span className="text-lg font-bold text-neutral-900">{client.leadScore || 0}/100</span>
                          </div>
                        </dd>
                      </div>

                      {client.leadSource && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500 mb-2">Lead Source</dt>
                          <dd className="mt-1">
                            <div className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-secondary-50 text-secondary-700 border border-secondary-200">
                              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {client.leadSource}
                            </div>
                          </dd>
                        </div>
                      )}

                      {/* Tags Section - Always show with edit option */}
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-neutral-500 mb-2 flex items-center justify-between">
                          <span>Tags</span>
                          <button
                            onClick={openTagsEdit}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                          >
                            <PencilIcon className="h-3 w-3" />
                            {client.tags && client.tags.length > 0 ? 'Edit' : 'Add Tags'}
                          </button>
                        </dt>
                        <dd className="mt-1">
                          {client.tags && client.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {client.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-secondary-100 to-secondary-200 text-secondary-800 border border-secondary-200"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-neutral-400 italic">No tags added</span>
                          )}
                        </dd>
                      </div>

                      {/* Contact Dates Section - Always show with edit option */}
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-neutral-500 mb-2 flex items-center justify-between">
                          <span>Contact Dates</span>
                          <button
                            onClick={openDateEdit}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                          >
                            <PencilIcon className="h-3 w-3" />
                            {(client.lastContactDate || client.nextFollowUpDate) ? 'Edit' : 'Add Dates'}
                          </button>
                        </dt>
                        <dd className="mt-1">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Last Contact */}
                            <div className="flex items-center text-sm">
                              <CalendarIcon className="h-4 w-4 mr-2 text-neutral-400" />
                              <div>
                                <span className="text-xs text-neutral-500 block">Last Contact</span>
                                {client.lastContactDate ? (
                                  <span className="font-medium text-neutral-900">
                                    {new Date(client.lastContactDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span className="text-neutral-400 italic">Not set</span>
                                )}
                              </div>
                            </div>

                            {/* Next Follow-up */}
                            <div className="flex items-center text-sm">
                              <CalendarIcon className="h-4 w-4 mr-2 text-warning-500" />
                              <div>
                                <span className="text-xs text-neutral-500 block">Next Follow-up</span>
                                {client.nextFollowUpDate ? (
                                  <span className="font-semibold text-warning-600">
                                    {new Date(client.nextFollowUpDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })}
                                  </span>
                                ) : (
                                  <span className="text-neutral-400 italic">Not set</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>
              </div>

              {/* Company & Professional Information (Phase 1.1) */}
              {(client.industry || client.companySize || client.annualRevenue || client.jobTitle || client.department) && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-neutral-900">Company & Professional Information</h3>
                  </div>
                  <div className="border-t border-neutral-200 px-4 py-5 sm:px-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      {client.industry && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Industry</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.industry}</dd>
                        </div>
                      )}

                      {client.companySize && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Company Size</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.companySize} employees</dd>
                        </div>
                      )}

                      {client.annualRevenue && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Annual Revenue</dt>
                          <dd className="mt-1 text-sm text-neutral-900">${client.annualRevenue.toLocaleString()}</dd>
                        </div>
                      )}

                      {client.jobTitle && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Job Title</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.jobTitle}</dd>
                        </div>
                      )}

                      {client.department && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Department</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.department}</dd>
                        </div>
                      )}

                      {client.isDecisionMaker && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Decision Maker</dt>
                          <dd className="mt-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                              Yes
                            </span>
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}

              {/* Social Links & Preferences (Phase 1.1) */}
              {(client.website || client.linkedinUrl || client.twitterUrl || client.facebookUrl || client.preferredContactMethod || client.timeZone) && (
                <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-6">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-neutral-900">Social Links & Preferences</h3>
                  </div>
                  <div className="border-t border-neutral-200 px-4 py-5 sm:px-6">
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                      {client.website && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Website</dt>
                          <dd className="mt-1 text-sm">
                            <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                              {client.website}
                            </a>
                          </dd>
                        </div>
                      )}

                      {client.linkedinUrl && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">LinkedIn</dt>
                          <dd className="mt-1 text-sm">
                            <a href={client.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                              View Profile
                            </a>
                          </dd>
                        </div>
                      )}

                      {client.twitterUrl && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Twitter</dt>
                          <dd className="mt-1 text-sm">
                            <a href={client.twitterUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                              View Profile
                            </a>
                          </dd>
                        </div>
                      )}

                      {client.facebookUrl && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Facebook</dt>
                          <dd className="mt-1 text-sm">
                            <a href={client.facebookUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">
                              View Profile
                            </a>
                          </dd>
                        </div>
                      )}

                      {client.preferredContactMethod && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Preferred Contact Method</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.preferredContactMethod}</dd>
                        </div>
                      )}

                      {client.timeZone && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Time Zone</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.timeZone}</dd>
                        </div>
                      )}

                      {client.languagePreference && (
                        <div>
                          <dt className="text-sm font-medium text-neutral-500">Language Preference</dt>
                          <dd className="mt-1 text-sm text-neutral-900">{client.languagePreference}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Stats Sidebar */}
            <div>
              {/* Stats Card */}
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl shadow-lg p-6 border border-primary-200">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-lg bg-primary-500 flex items-center justify-center">
                        <CurrencyDollarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-neutral-600">Total Deals</p>
                        <p className="text-2xl font-bold text-neutral-900">{client.deals?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-lg bg-warning-500 flex items-center justify-center">
                        <SparklesIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-neutral-600">Importance</p>
                        <p className="text-sm font-bold text-neutral-900">{client.importance.replace('_', ' ')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-lg bg-secondary-500 flex items-center justify-center">
                        <CalendarIcon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-neutral-600">Member Since</p>
                        <p className="text-sm font-bold text-neutral-900">
                          {new Date(client.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Card */}
              <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 border border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowAddActivity(true)}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-neutral-700 bg-neutral-50 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <PhoneIcon className="h-5 w-5 mr-3 text-primary-600" />
                    Log a Call
                  </button>
                  <button
                    onClick={() => setShowEmailComposer(true)}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-neutral-700 bg-neutral-50 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <EnvelopeIcon className="h-5 w-5 mr-3 text-secondary-600" />
                    Send Email
                  </button>
                  <button
                    onClick={() => setShowAddActivity(true)}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-neutral-700 bg-neutral-50 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <CalendarIcon className="h-5 w-5 mr-3 text-success-600" />
                    Schedule Meeting
                  </button>
                  <button
                    onClick={() => navigate(getClientPath(`/deals/new`), { state: { preselectedClientId: Number(id) } })}
                    className="w-full flex items-center px-4 py-3 text-sm font-medium text-neutral-700 bg-neutral-50 hover:bg-neutral-100 rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-3 text-warning-600" />
                    Create Deal
                  </button>
                </div>
              </div>

              {/* Client Segments */}
              <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 border border-neutral-200">
                <ClientSegments clientId={Number(id)} />
              </div>
            </div>
          </>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-neutral-900">
                  Activity Timeline
                </h3>
                <button
                  onClick={() => setShowAddActivity(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Log Activity
                </button>
              </div>
              <div className="border-t border-neutral-200 px-4 py-5 sm:px-6">
                <ActivityTimeline
                  key={activityRefreshKey}
                  clientId={Number(id)}
                  onActivityCreated={() => {
                    setActivityRefreshKey(prev => prev + 1);
                    fetchClient(); // Refresh client data to update lastContactDate and totalInteractions
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-neutral-900">
                  Deals ({client.deals?.length || 0})
                </h3>
                <button
                  onClick={() => navigate(getClientPath(`/deals/new`), { state: { preselectedClientId: Number(id) } })}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Deal
                </button>
              </div>
              <div className="border-t border-neutral-200">
                {client.deals && client.deals.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Pipeline / Stage
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Probability
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th scope="col" className="relative px-6 py-3">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {client.deals.map((deal) => (
                          <tr key={deal.id} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <CurrencyDollarIcon className="h-5 w-5 text-success-500 mr-2" />
                                <span className="text-sm font-semibold text-neutral-900">
                                  {deal.currency} {parseFloat(deal.amount).toLocaleString()}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-neutral-900">{deal.description}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {deal.stage ? (
                                <div className="flex flex-col space-y-1">
                                  <div className="text-xs text-neutral-500">
                                    {deal.pipeline?.name || 'No Pipeline'}
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: deal.stage.color || '#6B7280' }}
                                    />
                                    <span className="text-sm font-medium text-neutral-900">
                                      {deal.stage.name}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-neutral-400">Not assigned</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {deal.probability !== null && deal.probability !== undefined ? (
                                <div className="flex items-center space-x-2">
                                  <div className="flex-1 bg-neutral-200 rounded-full h-2 w-16">
                                    <div
                                      className="bg-success-500 h-2 rounded-full"
                                      style={{ width: `${deal.probability}%` }}
                                    />
                                  </div>
                                  <span className="text-sm font-medium text-neutral-700">
                                    {deal.probability}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-sm text-neutral-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                              {new Date(deal.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => {
                                  setSelectedDealForQuote(deal);
                                  setShowQuoteModal(true);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-gradient-to-r from-secondary-600 to-primary-600 hover:from-secondary-700 hover:to-primary-700"
                              >
                                <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                                Create Quote
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CurrencyDollarIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-medium text-neutral-900">No deals</h3>
                    <p className="mt-1 text-sm text-neutral-500">Get started by creating a new deal.</p>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate(getClientPath(`/deals/new`), { state: { preselectedClientId: Number(id) } })}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        Create your first deal
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Samples Tab */}
        {activeTab === 'samples' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-neutral-900">
                  Samples ({samples.length})
                </h3>
                <Link
                  to={getClientPath(`/clients/${id}/samples/new`)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add Sample
                </Link>
              </div>
              <div className="border-t border-neutral-200">
                {loadingSamples ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="mt-2 text-sm text-neutral-500">Loading samples...</p>
                  </div>
                ) : samples.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Tracking #
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Product
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Shipped
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {samples.map((sample) => (
                          <tr
                            key={sample.id}
                            onClick={() => navigate(getClientPath(`/clients/${id}/samples/${sample.id}`))}
                            className="hover:bg-neutral-50 cursor-pointer transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-secondary-100">
                                  <BeakerIcon className="h-6 w-6 text-secondary-600" />
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-neutral-900">
                                    {sample.trackingNumber}
                                  </div>
                                  <div className="text-xs text-neutral-500">
                                    Lot: {sample.lotNumber}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-neutral-900">{sample.productName}</div>
                              {sample.productSku && (
                                <div className="text-xs text-neutral-500">SKU: {sample.productSku}</div>
                              )}
                              {sample.supplier && (
                                <div className="text-xs text-neutral-500 mt-1">
                                  Supplier: {sample.supplier.name}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col gap-1">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sample.status === 'DELIVERED' ? 'bg-success-100 text-success-800' :
                                  sample.status === 'SHIPPED' ? 'bg-primary-100 text-primary-800' :
                                    sample.status === 'REQUESTED' ? 'bg-violet-100 text-violet-800' :
                                    sample.status === 'APPROVED' ? 'bg-success-100 text-success-800' :
                                      sample.status === 'REJECTED' ? 'bg-error-100 text-error-800' :
                                        sample.status === 'UNDER_REVIEW' ? 'bg-warning-100 text-warning-800' :
                                          'bg-neutral-100 text-neutral-800'
                                  }`}>
                                  {sample.status.replace('_', ' ')}
                                </span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                                  {sample.sampleType.replace('_', ' ')}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900 font-medium">
                                {sample.quantity} {sample.unit}
                              </div>
                              {sample.totalCost && (
                                <div className="text-xs text-neutral-500">
                                  {sample.currency} {parseFloat(sample.totalCost).toFixed(2)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-neutral-900">
                                {sample.shippingDate ? new Date(sample.shippingDate).toLocaleDateString() : '-'}
                              </div>
                              {sample.estimatedDeliveryDate && (
                                <div className="text-xs text-neutral-500">
                                  Est: {new Date(sample.estimatedDeliveryDate).toLocaleDateString()}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-2">
                                <Link
                                  to={getClientPath(`/clients/${id}/samples/${sample.id}`)}
                                  className="text-primary-600 hover:text-primary-900"
                                >
                                  View
                                </Link>
                                {sample.status === 'APPROVED' && client.deals && client.deals.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Use the first deal for this client
                                      if (client.deals && client.deals.length > 0) {
                                        const deal = client.deals[0];
                                        setSelectedDealForQuote(deal);
                                        setShowQuoteModal(true);
                                      }
                                    }}
                                    className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-gradient-to-r from-secondary-600 to-primary-600 hover:from-secondary-700 hover:to-primary-700"
                                  >
                                    <DocumentDuplicateIcon className="h-3 w-3 mr-1" />
                                    Quote
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BeakerIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-medium text-neutral-900">No samples</h3>
                    <p className="mt-1 text-sm text-neutral-500">Get started by sending a sample to this client.</p>
                    <div className="mt-6">
                      <Link
                        to={getClientPath(`/clients/${id}/samples/new`)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                        Send your first sample
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow-xl ring-1 ring-neutral-900/5 rounded-xl overflow-hidden">
              <div className="px-6 py-5 border-b border-neutral-200 bg-gradient-to-r from-secondary-50 to-primary-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                      <DocumentDuplicateIcon className="h-5 w-5 mr-2 text-secondary-600" />
                      Quotes & Proposals ({quotes.length})
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      All standalone and deal-linked quotes for this client
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedDealForQuote(null);
                      setShowQuoteModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Quote
                  </button>
                </div>
              </div>

              <div className="px-6 py-5">
                {loadingQuotes ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                    <p className="mt-2 text-sm text-neutral-500">Loading quotes...</p>
                  </div>
                ) : quotes.length === 0 ? (
                  <div className="text-center py-12">
                    <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-medium text-neutral-900">No quotes yet</h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      Create a standalone quote or add one from an existing deal.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quotes.map((quote) => (
                      <div
                        key={quote.id}
                        className="border border-neutral-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 bg-gradient-to-r from-white to-neutral-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="text-base font-semibold text-neutral-900">
                                {quote.quoteNumber}
                              </h4>
                              {getQuoteStatusBadge(quote.status)}
                              {quote.revisionNumber > 1 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-800">
                                  Rev {quote.revisionNumber}
                                </span>
                              )}
                            </div>

                            {quote.title && (
                              <p className="mt-1 text-sm text-neutral-600">{quote.title}</p>
                            )}

                            <p className="mt-1 text-xs text-neutral-500">
                              {quote.deal ? `Deal: ${quote.deal.description}` : 'Standalone quote'}
                            </p>

                            <div className="mt-3 grid grid-cols-3 gap-4">
                              <div>
                                <dt className="text-xs font-medium text-neutral-500">Total Amount</dt>
                                <dd className="mt-1 text-base font-bold text-secondary-600">
                                  {quote.currency} {Number(quote.total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs font-medium text-neutral-500">Items</dt>
                                <dd className="mt-1 text-sm text-neutral-900">
                                  {quote.items?.length || 0} item{quote.items?.length !== 1 ? 's' : ''}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-xs font-medium text-neutral-500">Created</dt>
                                <dd className="mt-1 text-sm text-neutral-900">
                                  {new Date(quote.createdAt).toLocaleDateString()}
                                </dd>
                              </div>
                            </div>

                            {quote.viewCount > 0 && (
                              <div className="mt-2 flex items-center text-xs text-neutral-500">
                                <EyeIcon className="h-4 w-4 mr-1" />
                                Viewed {quote.viewCount} time{quote.viewCount !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>

                          <div className="ml-4">
                            <button
                              onClick={() => setSelectedQuoteId(quote.id)}
                              className="inline-flex items-center px-3 py-1.5 border border-neutral-300 text-xs font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                            >
                              <EyeIcon className="h-3 w-3 mr-1" />
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="lg:col-span-3">
            <div className="bg-white shadow-xl ring-1 ring-neutral-900/5 rounded-xl overflow-hidden">
              <div className="px-6 py-5 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
                      <CheckCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
                      Tasks
                    </h3>
                    <p className="mt-1 text-sm text-neutral-600">
                      All tasks related to this client
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTaskModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Task
                  </button>
                </div>
              </div>

              <div className="px-6 py-5">
                <TaskList
                  key={taskRefreshKey}
                  clientId={Number(id)}
                  showFilters={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Activity Modal */}
      {showAddActivity && (
        <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <ActivityForm
            clientId={Number(id)}
            onSuccess={() => {
              setShowAddActivity(false);
              setActivityRefreshKey(prev => prev + 1);
              fetchClient(); // Refresh client data
            }}
            onCancel={() => setShowAddActivity(false)}
          />
        </div>
      )}

      {/* Email Composer Modal */}
      {showEmailComposer && (
        <EmailComposer
          client={client}
          onClose={() => setShowEmailComposer(false)}
          onSent={() => {
            setActivityRefreshKey(prev => prev + 1);
            fetchClient(); // Refresh client data
          }}
        />
      )}

      {/* Quote Modal */}
      {showQuoteModal && client && (
        <QuoteModal
          deal={selectedDealForQuote || undefined}
          client={client}
          onClose={() => {
            setShowQuoteModal(false);
            setSelectedDealForQuote(null);
          }}
          onSuccess={() => {
            fetchQuotes();
            setShowQuoteModal(false);
            setSelectedDealForQuote(null);
          }}
        />
      )}

      {/* Quote Detail Modal */}
      {selectedQuoteId && (
        <QuoteDetailModal
          quoteId={selectedQuoteId}
          onClose={() => setSelectedQuoteId(null)}
          onRefresh={() => {
            fetchQuotes();
            setSelectedQuoteId(null);
          }}
        />
      )}

      {/* Task Form Modal */}
      {showTaskModal && (
        <TaskFormModal
          onClose={() => setShowTaskModal(false)}
          onSuccess={() => {
            setTaskRefreshKey(prev => prev + 1);
            setShowTaskModal(false);
          }}
          defaultClientId={Number(id)}
        />
      )}

      {/* Edit Dates Modal */}
      {editingDates && (
        <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Edit Contact Dates</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="lastContactDate" className="block text-sm font-medium text-neutral-700 mb-1">
                  Last Contact Date
                </label>
                <input
                  type="date"
                  id="lastContactDate"
                  value={dateForm.lastContactDate}
                  onChange={(e) => setDateForm({ ...dateForm, lastContactDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="nextFollowUpDate" className="block text-sm font-medium text-neutral-700 mb-1">
                  Next Follow-up Date
                </label>
                <input
                  type="date"
                  id="nextFollowUpDate"
                  value={dateForm.nextFollowUpDate}
                  onChange={(e) => setDateForm({ ...dateForm, nextFollowUpDate: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelDateEdit}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDates}
                disabled={savingDates}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {savingDates ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tags Modal */}
      {editingTags && (
        <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Manage Tags</h3>

            {/* Add new tag input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add a new tag..."
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Add
              </button>
            </div>

            {/* Current tags */}
            <div className="mb-4">
              <p className="text-sm text-neutral-500 mb-2">Current Tags:</p>
              {tagsForm.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tagsForm.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-secondary-100 to-secondary-200 text-secondary-800 border border-secondary-200"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-secondary-600 hover:text-secondary-800"
                      >
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-400 italic">No tags added yet</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelTagsEdit}
                className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTags}
                disabled={savingTags}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {savingTags ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
